// frontend/src/pages/marketing/FreelancerSMSCreditsPage.js
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";
import { getStripePromise } from "../../utils/stripeLoader";
import "./SMSCreditsPage.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;
const stripePromise = getStripePromise(API_BASE_URL);

// Purchase form component for freelancers
const PurchaseForm = ({ shopId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [creditAmount, setCreditAmount] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [pricing, setPricing] = useState(null);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL;
  };

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/sms-credits/pricing`);
        if (res.ok) {
          const data = await res.json();
          setPricing(data.pricing);
          // Set initial credit amount to minimum purchase
          if (data.pricing?.minimumPurchase) {
            setCreditAmount(data.pricing.minimumPurchase);
          }
        }
      } catch (err) {
      }
    };
    fetchPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculatePrice = useCallback(() => {
    if (!pricing || !creditAmount) return null;

    let perCredit = pricing.basePrice;
    let discount = 0;

    // Find applicable bulk discount
    if (pricing.bulkDiscounts) {
      const applicableDiscounts = pricing.bulkDiscounts.filter(
        (d) => creditAmount >= d.minCredits,
      );
      if (applicableDiscounts.length > 0) {
        const bestDiscount =
          applicableDiscounts[applicableDiscounts.length - 1];
        perCredit = bestDiscount.pricePerCredit;
        discount = bestDiscount.discountPercent;
      }
    }

    const total = creditAmount * perCredit;
    return { total, perCredit, discount };
  }, [pricing, creditAmount]);

  const priceInfo = calculatePrice();

  if (!pricing) {
    return (
      <div style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>
        Loading pricing information...
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      // Create payment intent
      const response = await fetch(
        `${getApiBaseUrl()}/api/sms-credits/shop/${shopId}/purchase`,
        {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ creditAmount }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create payment");
      }

      const { clientSecret } = await response.json();

      // Confirm payment
      const { error: stripeError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Confirm purchase
      const confirmResponse = await fetch(
        `${getApiBaseUrl()}/api/sms-credits/shop/${shopId}/confirm-purchase`,
        {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ paymentIntentId: paymentIntent.id }),
        },
      );

      if (!confirmResponse.ok) {
        throw new Error("Payment succeeded but failed to add credits");
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: "500px" }}>
      {/* Amount Selection */}
      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            display: "block",
            fontWeight: "600",
            marginBottom: "8px",
            fontSize: "14px",
          }}
        >
          Number of Credits
        </label>
        <input
          type="number"
          min={pricing?.minimumPurchase}
          step="1"
          value={creditAmount || ""}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            setCreditAmount(isNaN(value) ? pricing?.minimumPurchase : value);
          }}
          style={{
            width: "100%",
            padding: "10px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "14px",
          }}
        />
      </div>

      {/* Pricing Display */}
      {priceInfo && (
        <div
          style={{
            background: "#f3f4f6",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
          }}
        >
          <div style={{ fontSize: "14px", marginBottom: "8px" }}>
            <strong>Price per credit:</strong> ${priceInfo.perCredit.toFixed(4)}
            {priceInfo.discount > 0 && (
              <span style={{ color: "#10b981", marginLeft: "8px" }}>
                ({priceInfo.discount}% off)
              </span>
            )}
          </div>
          <div style={{ fontSize: "18px", fontWeight: "600" }}>
            <strong>Total:</strong> ${priceInfo.total.toFixed(2)}
          </div>
        </div>
      )}

      {/* Bulk Discount Tiers */}
      {pricing?.bulkDiscounts && pricing.bulkDiscounts.length > 0 && (
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #3b82f6",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "24px",
            fontSize: "13px",
          }}
        >
          <div
            style={{ fontWeight: "600", marginBottom: "8px", color: "#1e40af" }}
          >
            💡 Bulk Discounts:
          </div>
          {pricing.bulkDiscounts.map((discount, index) => (
            <div key={index} style={{ marginBottom: "4px", color: "#1e3a8a" }}>
              {discount.minCredits.toLocaleString()}+ credits: $
              {discount.pricePerCredit.toFixed(4)}/credit (
              {discount.discountPercent}% off)
            </div>
          ))}
        </div>
      )}

      {/* Card Element */}
      <div
        style={{
          padding: "12px",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          marginBottom: "16px",
          background: "white",
        }}
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": { color: "#aab7c4" },
              },
              invalid: { color: "#9e2146" },
            },
          }}
        />
      </div>

      {error && (
        <div
          style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "16px",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: "12px" }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          style={{
            flex: 1,
            padding: "10px",
            background: "#6b7280",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: processing ? "not-allowed" : "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          style={{
            flex: 2,
            padding: "10px",
            background: processing ? "#9ca3af" : "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: !stripe || processing ? "not-allowed" : "pointer",
          }}
        >
          {processing ? "Processing..." : `Purchase ${creditAmount} Credits`}
        </button>
      </div>
    </form>
  );
};

// Main component
const FreelancerSMSCreditsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopId, setShopId] = useState(null);
  const [credits, setCredits] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [success, setSuccess] = useState(null);

  // Auto-replenish state
  const [autoReplenishEnabled, setAutoReplenishEnabled] = useState(false);
  const [autoReplenishThreshold, setAutoReplenishThreshold] = useState(50);
  const [autoReplenishAmount, setAutoReplenishAmount] = useState(500);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [savingAutoReplenish, setSavingAutoReplenish] = useState(false);
  const [minPurchase, setMinPurchase] = useState(150);
  const [basePrice, setBasePrice] = useState(0.067);
  const isBoothRenterPath = location.pathname.includes("/booth-renter/");
  const paymentMethodsPath = isBoothRenterPath
    ? "/dashboard/booth-renter/payment-methods"
    : "/dashboard/freelancer/payment-methods";
  const paymentMethodsLabel = isBoothRenterPath
    ? "Payment Methods"
    : "Billing & Payment Methods";

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    return process.env.REACT_APP_API_BASE_URL;
  }, []);

  const fetchSavedPaymentMethods = useCallback(
    async (headersOverride) => {
      const headers = headersOverride || getAuthHeaders();
      if (!headers) return null;

      try {
        const res = await fetch(
          `${getApiBaseUrl()}/api/subscription-new/payment-methods`,
          { headers },
        );

        if (!res.ok) {
          return null;
        }

        const data = await res.json();
        return (data.paymentMethods || []).length > 0;
      } catch (err) {
        return null;
      }
    },
    [getAuthHeaders, getApiBaseUrl],
  );

  const fetchShopId = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${getApiBaseUrl()}/api/shop/my-shops`, {
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        const shops = data.shops || data || [];
        if (shops.length > 0) {
          // Use first shop for freelancer
          const extractedShopId =
            typeof shops[0]._id === "object" ? shops[0]._id._id : shops[0]._id;
          setShopId(extractedShopId);
        }
      }
    } catch (err) {
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchCredits = useCallback(async () => {
    if (!shopId) return;

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(
        `${getApiBaseUrl()}/api/sms-credits/shop/${shopId}`,
        { headers },
      );

      if (res.ok) {
        const data = await res.json();
        setCredits(data.credits);

        // Load auto-replenish settings
        if (data.credits.autoReplenish) {
          setAutoReplenishEnabled(data.credits.autoReplenish.enabled || false);
          setAutoReplenishThreshold(data.credits.autoReplenish.threshold || 50);
          setAutoReplenishAmount(data.credits.autoReplenish.amount || 500);
        }

        // Use the dedicated payment methods API as the source of truth.
        const savedPaymentMethodStatus = await fetchSavedPaymentMethods(headers);
        setHasPaymentMethod(
          savedPaymentMethodStatus ?? data.credits.hasPaymentMethod ?? false,
        );

        // Load pricing info
        if (data.credits.pricing) {
          setMinPurchase(data.credits.pricing.minimumPurchase || 150);
          setBasePrice(data.credits.pricing.basePrice || 0.067);
        }
      } else {
        throw new Error("Failed to fetch credits");
      }
    } catch (err) {
      setError(err.message);
    }
  }, [shopId, getAuthHeaders, getApiBaseUrl, fetchSavedPaymentMethods]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchShopId();
      setLoading(false);
    };
    loadData();
  }, [fetchShopId]);

  useEffect(() => {
    if (shopId) {
      fetchCredits();
    }
  }, [shopId, fetchCredits]);

  useEffect(() => {
    if (!error) {
      return undefined;
    }

    const timeoutId = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timeoutId);
  }, [error]);

  const handlePurchaseSuccess = () => {
    setShowPurchase(false);
    setSuccess("Credits purchased successfully!");
    fetchCredits();
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleSaveAutoReplenish = async () => {
    if (!shopId) return;

    setSavingAutoReplenish(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      if (autoReplenishEnabled && !hasPaymentMethod) {
        setError(
          "Please add a payment method before enabling auto-replenish",
        );
        setSavingAutoReplenish(false);
        return;
      }

      if (autoReplenishAmount < minPurchase) {
        setError(`Purchase amount must be at least ${minPurchase} credits`);
        setSavingAutoReplenish(false);
        return;
      }

      const res = await fetch(
        `${getApiBaseUrl()}/api/sms-credits/shop/${shopId}/auto-replenish`,
        {
          method: "PUT",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            enabled: autoReplenishEnabled,
            threshold: autoReplenishThreshold,
            amount: autoReplenishAmount,
          }),
        },
      );

      if (res.ok) {
        setSuccess("Auto-replenish settings saved!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to save settings");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSavingAutoReplenish(false);
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "24px", textAlign: "center" }}>
          <p>Loading...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  if (error && !credits) {
    return (
      <FreelancerSidebarLayout>
        <div
          style={{
            padding: "24px",
            background: "#fee2e2",
            color: "#991b1b",
            borderRadius: "8px",
          }}
        >
          {error}
        </div>
      </FreelancerSidebarLayout>
    );
  }

  if (!shopId) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "24px" }}>
          <p>No shop found for your account.</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  const totalCredits = credits
    ? credits.freeCredits + credits.purchasedCredits
    : 0;
  const freeCreditsUsed = credits
    ? credits.monthlyAllowance - credits.freeCredits
    : 0;
  const freeCreditsPercent = credits?.monthlyAllowance
    ? (credits.freeCredits / credits.monthlyAllowance) * 100
    : 0;
  const nextResetLabel = credits?.nextResetDate
    ? new Date(credits.nextResetDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <FreelancerSidebarLayout>
      <div style={{ padding: "24px", maxWidth: "900px" }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{ fontSize: "28px", fontWeight: "700", marginBottom: "8px" }}
          >
            SMS Credits
          </h1>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            Manage your SMS credits for booking notifications
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div
            style={{
              background: "#d1fae5",
              color: "#065f46",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "24px",
              fontSize: "14px",
            }}
          >
            ✓ {success}
          </div>
        )}

        {error && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: isBoothRenterPath ? "flex-start" : "center",
              gap: "16px",
              flexWrap: "wrap",
              background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
              border: "1px solid #fdba74",
              borderRadius: "14px",
              padding: "18px 20px",
              marginBottom: "24px",
              boxShadow: "0 8px 24px rgba(245, 158, 11, 0.08)",
            }}
          >
            <div style={{ flex: 1, minWidth: "260px" }}>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#9a3412",
                  marginBottom: "6px",
                }}
              >
                Payment method needed for auto-replenish
              </div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#7c2d12",
                  lineHeight: 1.6,
                }}
              >
                {error}
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {!hasPaymentMethod && (
                <button
                  onClick={() => navigate(paymentMethodsPath)}
                  style={{
                    padding: "10px 16px",
                    background: "#ea580c",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Open {paymentMethodsLabel}
                </button>
              )}
              <button
                onClick={() => setError(null)}
                style={{
                  padding: "10px 16px",
                  background: "white",
                  color: "#9a3412",
                  border: "1px solid #fdba74",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Total Credits Overview */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              marginBottom: "20px",
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                Total Available Credits
              </h2>
              <p style={{ fontSize: "13px", color: "#6b7280" }}>
                Free credits + Purchased credits
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "700",
                  color: "#3b82f6",
                }}
              >
                {totalCredits.toLocaleString()}
              </div>
              <div
                style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
              >
                credits remaining
              </div>
            </div>
          </div>

          {/* Warning indicators */}
          {totalCredits < credits?.urgentThreshold && (
            <div
              style={{
                background: "#fee2e2",
                color: "#991b1b",
                padding: "10px 12px",
                borderRadius: "6px",
                fontSize: "13px",
                marginBottom: "16px",
              }}
            >
              ⚠️ Urgent: Credits critically low! Purchase more to avoid service
              interruption.
            </div>
          )}
          {totalCredits >= credits?.urgentThreshold &&
            totalCredits < credits?.lowThreshold && (
              <div
                style={{
                  background: "#fef3c7",
                  color: "#92400e",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  fontSize: "13px",
                  marginBottom: "16px",
                }}
              >
                ⚠️ Low credits: Consider purchasing more to maintain
                uninterrupted service.
              </div>
            )}

          <button
            onClick={() => setShowPurchase(true)}
            style={{
              padding: "10px 20px",
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Purchase More Credits
          </button>
        </div>

        {/* Credits Breakdown */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          {/* Free Credits */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "12px",
              }}
            >
              Free Monthly Credits
            </h3>
            <div
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "#10b981",
                marginBottom: "8px",
              }}
            >
              {credits?.freeCredits?.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "12px",
              }}
            >
              {freeCreditsUsed} used of {credits?.monthlyAllowance} this month
            </div>
            {/* Progress bar */}
            <div
              style={{
                background: "#e5e7eb",
                height: "8px",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background:
                    freeCreditsPercent > 50
                      ? "#10b981"
                      : freeCreditsPercent > 25
                        ? "#f59e0b"
                        : "#ef4444",
                  height: "100%",
                  width: `${freeCreditsPercent}%`,
                  transition: "width 0.3s ease",
                }}
              ></div>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#9ca3af",
                marginTop: "8px",
                fontStyle: "italic",
              }}
            >
              {nextResetLabel
                ? `Next reset: ${nextResetLabel}`
                : "Credits reset monthly"}
            </div>
          </div>

          {/* Purchased Credits */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "12px",
              }}
            >
              Purchased Credits
            </h3>
            <div
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "#3b82f6",
                marginBottom: "8px",
              }}
            >
              {credits?.purchasedCredits?.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "12px",
              }}
            >
              Never expires
            </div>
            <div style={{ fontSize: "13px", color: "#6b7280" }}>
              Used when free credits run out
            </div>
          </div>
        </div>

        {/* Auto-Replenish Settings */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
            marginTop: "24px",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              🔄 Auto-Replenish Settings
            </h3>
            <p style={{ fontSize: "13px", color: "#6b7280" }}>
              Automatically purchase credits when your balance falls below a
              threshold
            </p>
          </div>

          {/* Enable Toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "20px",
              padding: "16px",
              background: "#f9fafb",
              borderRadius: "8px",
            }}
          >
            <input
              type="checkbox"
              id="autoReplenishToggle"
              checked={autoReplenishEnabled}
              onChange={(e) => setAutoReplenishEnabled(e.target.checked)}
              style={{
                width: "20px",
                height: "20px",
                marginRight: "12px",
                cursor: "pointer",
              }}
            />
            <label
              htmlFor="autoReplenishToggle"
              style={{
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              Enable automatic credit purchases
            </label>
          </div>

          {/* Settings (shown when enabled) */}
          {autoReplenishEnabled && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    marginBottom: "8px",
                  }}
                >
                  Threshold (credits)
                </label>
                <input
                  type="number"
                  min="10"
                  step="10"
                  value={autoReplenishThreshold}
                  onChange={(e) =>
                    setAutoReplenishThreshold(parseInt(e.target.value) || 50)
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  placeholder="e.g., 50"
                />
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  Buy more when credits fall below this amount
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    marginBottom: "8px",
                  }}
                >
                  Purchase Amount (credits)
                </label>
                <input
                  type="number"
                  min={minPurchase}
                  step="50"
                  value={autoReplenishAmount}
                  onChange={(e) =>
                    setAutoReplenishAmount(
                      parseInt(e.target.value) || minPurchase,
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                  placeholder={`e.g., ${minPurchase}`}
                />
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  Minimum: {minPurchase} credits ($
                  {(minPurchase * basePrice).toFixed(2)})
                </div>
              </div>
            </div>
          )}

          {/* Payment Method */}
          <div
            style={{
              padding: "16px",
              background: hasPaymentMethod ? "#d1fae5" : "#fef3c7",
              border: `1px solid ${hasPaymentMethod ? "#10b981" : "#f59e0b"}`,
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              {hasPaymentMethod
                ? "✓ Payment Method Available"
                : "⚠️ Payment Method Required"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: hasPaymentMethod ? "0" : "12px",
              }}
            >
              {hasPaymentMethod
                ? "Using your saved payment method for automatic purchases"
                : "Add a payment method to enable automatic purchases"}
            </div>
            {!hasPaymentMethod && (
              <button
                onClick={() => navigate(paymentMethodsPath)}
                style={{
                  padding: "8px 16px",
                  background: "#f59e0b",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Open {paymentMethodsLabel}
              </button>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveAutoReplenish}
            disabled={savingAutoReplenish}
            style={{
              padding: "10px 24px",
              background: savingAutoReplenish ? "#9ca3af" : "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: savingAutoReplenish ? "not-allowed" : "pointer",
            }}
          >
            {savingAutoReplenish ? "Saving..." : "Save Settings"}
          </button>
        </div>

        {/* How It Works */}
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginTop: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            How Credits Work
          </h3>
          <div
            style={{ fontSize: "13px", color: "#6b7280", lineHeight: "1.6" }}
          >
            <p style={{ marginBottom: "8px" }}>
              • <strong>Free credits</strong> reset to{" "}
              {credits?.monthlyAllowance} on the 1st of each month
            </p>
            <p style={{ marginBottom: "8px" }}>
              • <strong>Free credits are used first</strong> for all SMS sends
            </p>
            <p style={{ marginBottom: "8px" }}>
              • <strong>Purchased credits are used</strong> when free credits
              run out
            </p>
            <p>
              • <strong>Purchased credits never expire</strong> and roll over
              indefinitely
            </p>
          </div>
        </div>

        {/* Purchase Modal */}
        {showPurchase && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowPurchase(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: "600px",
                width: "90%",
                maxHeight: "90vh",
                overflow: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "20px",
                }}
              >
                Purchase SMS Credits
              </h2>
              <Elements stripe={stripePromise}>
                <PurchaseForm
                  shopId={shopId}
                  onSuccess={handlePurchaseSuccess}
                  onCancel={() => setShowPurchase(false)}
                />
              </Elements>
            </div>
          </div>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
};

export default FreelancerSMSCreditsPage;
